import type { FunctionComponent, DragEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Center, Loader, Stack, Text } from '@mantine/core';

import { useTranslation } from 'i18n';

import { ImageIconLg, WarningIconSm } from '@chownow/pantry/icons';

const NS = 'menuPhotosPhotoDrawer';

const MAX_FILE_SIZE = 10_000_000;
const MAX_WIDTH = 5456;
const MAX_HEIGHT = 4092;
const ACCEPTED_EXTENSIONS = ['.gif', '.heic', '.heif', '.jpg', '.jpeg', '.png', '.webp'];
// Check both to prevent a renamed file (e.g. malware.exe → malware.jpg)
// bypassing the extension check.
const ACCEPTED_MIME_TYPES = [
  'image/gif',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/webp',
];

function isAcceptedType(file: File) {
  return (
    ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext)) &&
    ACCEPTED_MIME_TYPES.includes(file.type)
  );
}

type UploadStatus = 'idle' | 'preparing' | 'uploading';

type AddPhotoContentProps = {
  opened: boolean;
  itemName: string;
  onUpload?: (file: File) => Promise<void>;
  onSuccess: () => void;
};

export const AddPhotoContent: FunctionComponent<AddPhotoContentProps> = ({
  opened,
  itemName,
  onUpload = () => Promise.resolve(),
  onSuccess,
}) => {
  const { t } = useTranslation(NS);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCountRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetState = () => {
    setStatus('idle');
    setErrorMessage(null);
    setIsDragging(false);
    dragCountRef.current = 0;
    if (inputRef.current) inputRef.current.value = '';
  };

  useEffect(() => {
    if (!opened) return;
    setStatus('idle');
    setErrorMessage(null);
    setIsDragging(false);
    dragCountRef.current = 0;
    if (inputRef.current) inputRef.current.value = '';
  }, [opened]);

  const handleFile = async (file: File) => {
    setErrorMessage(null);

    if (!isAcceptedType(file)) {
      setErrorMessage(t('AddPhotoContent.typeError'));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage(t('AddPhotoContent.sizeError'));
      return;
    }

    setStatus('preparing');

    // Chrome cannot decode HEIC/HEIF via new Image() — skip dimension check for those formats.
    const skipDimensionCheck = ['image/heic', 'image/heif'].includes(file.type);

    if (!skipDimensionCheck) {
      const objectUrl = URL.createObjectURL(file);
      try {
        const img = new window.Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = objectUrl;
        });

        if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
          setErrorMessage(t('AddPhotoContent.dimensionMaxError'));
          setStatus('idle');
          return;
        }
      } catch {
        setErrorMessage(t('AddPhotoContent.readError'));
        setStatus('idle');
        return;
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    }

    setStatus('uploading');
    try {
      await onUpload(file);
      resetState();
      onSuccess();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : t('AddPhotoContent.uploadError'));
      setStatus('idle');
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCountRef.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
    dragCountRef.current -= 1;
    if (dragCountRef.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCountRef.current = 0;
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) void handleFile(dropped);
  };

  const isLoading = status === 'preparing' || status === 'uploading';

  return (
    <Stack gap="md">
      {errorMessage && (
        <Alert variant="caution" icon={<WarningIconSm />}>
          {errorMessage}
        </Alert>
      )}
      <Stack
        align="center"
        justify="center"
        gap="sm"
        p="xl"
        bg={isDragging ? 'var(--surface-tertiary)' : 'var(--surface-secondary)'}
        bd="1px solid var(--border-secondary)"
        w="100%"
        style={{
          borderRadius: 'var(--mantine-radius-sm)',
          overflow: 'hidden',
          aspectRatio: '4/3',
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          data-testid="add-photo-file-input"
          style={{ display: 'none' }}
          onChange={e => {
            const selected = e.target.files?.[0];
            if (selected) void handleFile(selected);
          }}
        />
        {isLoading ? (
          <>
            <Loader size="md" />
            <Text variant="sublabel-sm" c="var(--content-secondary)">
              {status === 'preparing'
                ? t('AddPhotoContent.preparing')
                : t('AddPhotoContent.uploading')}
            </Text>
          </>
        ) : (
          <>
            <ImageIconLg />
            <Text variant="sublabel-sm" c="var(--content-primary)" visibleFrom="md">
              {t('AddPhotoContent.dropPrompt')}
            </Text>
            <Center>
              <Button
                type="button"
                radius="xl"
                tt="uppercase"
                bg="var(--surface-inverse-primary)"
                c="var(--content-inverse-primary)"
                style={{ letterSpacing: 'var(--letterspacing-wide)' }}
                onClick={() => inputRef.current?.click()}
              >
                {t('AddPhotoContent.browse')}
              </Button>
            </Center>
          </>
        )}
      </Stack>
      <Text>
        {t('AddPhotoContent.instructionsPrefix')}
        {itemName && (
          <>
            {' '}
            <Text component="span" variant="body-sm-bold">
              &lsquo;{itemName}&rsquo;
            </Text>
          </>
        )}
        {t('AddPhotoContent.instructionsSuffix')}
      </Text>
    </Stack>
  );
};
